import { useState } from "react";
import type { Category, LedgerAccount } from "../../entities/ledger/types";
import { DateField } from "../../shared/ui/DateField";
import { Modal } from "../../shared/ui/Modal";
import { Select } from "../../shared/ui/Select";
import { describe, periodPresets, type Period } from "./period";

interface JournalFiltersProps {
  period: Period;
  onPeriod: (period: Period) => void;
  accounts?: LedgerAccount[];
  accountID?: string;
  onAccount?: (id: string) => void;
  categories?: Category[];
  categoryID?: string;
  onCategory?: (id: string) => void;
}

// The sentinel the server understands for "carries no category at all".
const UNCATEGORIZED = "none";

// The period is the one thing every journal view shares. The screen shows
// only what is currently selected; changing it happens in a dialog, so no
// form controls sit on the reading surface and nothing is displaced.
export function JournalFilters({ period, onPeriod, accounts, accountID = "", onAccount, categories, categoryID = "", onCategory }: JournalFiltersProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(period);
  const [draftAccount, setDraftAccount] = useState(accountID);
  const [draftCategory, setDraftCategory] = useState(categoryID);
  const account = accounts?.find((candidate) => candidate.id === accountID);
  const categoryLabel = categoryID === "" ? "All categories"
    : categoryID === UNCATEGORIZED ? "Uncategorized"
      : categories?.find((candidate) => candidate.id === categoryID)?.name ?? categoryID;
  const valid = draft.from <= draft.to;

  function start() {
    setDraft(period);
    setDraftAccount(accountID);
    setDraftCategory(categoryID);
    setOpen(true);
  }

  function apply() {
    if (!valid) return;
    onPeriod(draft);
    if (onAccount && draftAccount !== accountID) onAccount(draftAccount);
    if (onCategory && draftCategory !== categoryID) onCategory(draftCategory);
    setOpen(false);
  }

  return (
    <>
      <div className="filter-summary">
        <span className="filter-facts">
          <span className="filter-fact"><small>Period</small><strong>{describe(period)}</strong></span>
          {accounts ? <span className="filter-fact"><small>Account</small><strong>{account ? account.name : "All accounts"}</strong></span> : null}
          {categories ? <span className="filter-fact"><small>Category</small><strong>{categoryLabel}</strong></span> : null}
        </span>
        <button className="secondary-button" type="button" onClick={start}>Change</button>
      </div>

      <Modal
        open={open} onClose={() => setOpen(false)} title="Period"
        subtitle="Both days are included. The journal reads the same range on every view."
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setOpen(false)}>Cancel</button>
          <button className="primary-button compact" type="button" onClick={apply} disabled={!valid}>
            <span>Apply</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        {accounts && onAccount ? (
          <div className="field">
            <span className="field-label" id="journal-account-label">Account</span>
            <Select
              labelledBy="journal-account-label" value={draftAccount} onChange={setDraftAccount}
              options={[{ value: "", label: "All accounts" }, ...accounts.map((candidate) => ({
                value: candidate.id,
                label: candidate.displayLabel ? `${candidate.name} · ${candidate.displayLabel}` : candidate.name,
                hint: candidate.currency,
              }))]}
            />
          </div>
        ) : null}
        {categories && onCategory ? (
          <div className="field">
            <span className="field-label" id="journal-category-label">Category</span>
            <Select
              labelledBy="journal-category-label" value={draftCategory} onChange={setDraftCategory}
              options={[
                { value: "", label: "All categories" },
                { value: UNCATEGORIZED, label: "Uncategorized" },
                ...categories.map((category) => ({ value: category.id, label: category.name })),
              ]}
            />
          </div>
        ) : null}
        <div className="field-grid">
          <DateField label="From" value={draft.from} max={draft.to} rangeFrom={draft.to} onChange={(from) => setDraft({ ...draft, from })} />
          <DateField label="To" value={draft.to} min={draft.from} rangeFrom={draft.from} onChange={(to) => setDraft({ ...draft, to })} />
        </div>
        <div className="field">
          <span className="field-label">Quick ranges</span>
          <div className="chip-group period-presets">
            {periodPresets.map((preset) => <button className="chip" type="button" key={preset.label} onClick={() => setDraft(preset.value())}>{preset.label}</button>)}
          </div>
        </div>
        {valid ? null : <p className="form-error" role="alert">The period must start before it ends.</p>}
      </Modal>
    </>
  );
}
